import {
  Component,
  computed,
  ElementRef,
  inject,
  LOCALE_ID,
  NgZone,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { AuthService, BaseComponent } from 'tms-lib';
import { HelpChatRoles } from '../../../constants';
import { HelpChatMessage, HelpOrderDraft, HelpPurchaseOrderDraft } from '../../../models';
import { HelpService } from '../../../services';

@Component({
  selector: 'app-help-panel',
  templateUrl: './help-panel.component.html',
  styleUrl: './help-panel.component.scss',
  imports: [AsyncPipe, FormsModule],
})
export class HelpPanelComponent extends BaseComponent implements OnInit {
  // How many conversation messages ride with each question. Sized for the guided
  // order flow, where one order line takes several short turns — 6 was enough for
  // one-shot questions but made Stan "forget" the customer and earlier lines mid-order
  // (they slid out of the window). The server caps this again as a backstop.
  private static readonly historyLimit = 24;
  private static readonly guidOrNumber = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

  // The only route prefixes a data answer's [text](/route) link may navigate to.
  // Anything else — external URLs, a link-shaped string inside the data itself —
  // stays escaped text exactly as before.
  private static readonly linkPrefixes = ['/Sales/', '/Purchasing/', '/Stock/', '/Production/', '/Analysis/'];

  private readonly helpService = inject(HelpService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly locale = inject(LOCALE_ID);
  private readonly zone = inject(NgZone);
  private readonly messageList = viewChild<ElementRef<HTMLDivElement>>('messageList');

  // Voice rides entirely on the browser's Web Speech APIs - no server, no vendor, no
  // usage cost. Both features hide their controls where the browser lacks support.
  private static readonly speechRecognitionCtor: any =
    (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  protected readonly dictationSupported = !!HelpPanelComponent.speechRecognitionCtor;
  protected readonly speechSupported = 'speechSynthesis' in window;

  protected readonly isAuthenticated$: Observable<boolean> = this.authService.isAuthenticated();

  // The logged-in user's first name, for the greeting. Falls back to a plain
  // greeting when the profile only carries something email-shaped.
  protected readonly firstName$: Observable<string> = this.authService.getUser().pipe(
    map((user) => {
      const name = ((user?.given_name ?? user?.full_name ?? user?.name ?? '') as string)
        .replace(' @', '@')
        .split(' ')[0];
      return name && !name.includes('@') ? name : '';
    }),
  );
  protected readonly open = signal(false);
  protected readonly expanded = signal(false);
  protected readonly busy = signal(false);
  protected readonly draft = signal<HelpOrderDraft | undefined>(undefined);
  protected readonly purchaseDraft = signal<HelpPurchaseOrderDraft | undefined>(undefined);
  protected readonly roles = HelpChatRoles;

  // Dictation state: the mic is push-to-talk and auto-stops on a silence pause. The
  // transcript only ever lands in the INPUT BOX - a misheard quantity must pass the
  // user's eyes before it reaches Stan; nothing is auto-sent.
  protected readonly listening = signal(false);

  // Read replies aloud - session-only and OFF by default (Stan lives in open-plan
  // offices).
  protected readonly speakReplies = signal(false);

  private recognition: any;
  // The typed text present when dictation started, so dictating appends rather than
  // overwrites.
  private dictationBase = '';

  // The assistant is a vendor-controlled per-site feature: the Ask Stan button only
  // renders on sites where TMS has switched it on. The server enforces this again.
  protected readonly enabled = signal(false);

  // Month-to-date percentage of the site's assistant allowance, once past the
  // warning threshold — shown as a footer line so the wording stays localised.
  protected readonly warningPercent = signal<number | undefined>(undefined);

  protected question = '';
  protected messages: HelpChatMessage[] = [];
  protected messageHtml: string[] = [];

  // Both the panel (by its header) and the launcher button are draggable so they
  // can be moved off whatever they are covering. Offsets from their anchored
  // positions are applied as transforms — per-drag values, so style bindings
  // rather than classes.
  private static readonly dragThreshold = 6;

  private readonly panelOffset = signal({ x: 0, y: 0 });
  protected readonly panelTransform = computed(() => `translate(${this.panelOffset().x}px, ${this.panelOffset().y}px)`);

  private readonly launcherOffset = signal({ x: 0, y: 0 });
  protected readonly launcherTransform = computed(
    () => `translate(${this.launcherOffset().x}px, ${this.launcherOffset().y}px)`,
  );
  private launcherDragged = false;

  ngOnInit() {
    this.authService
      .isAuthenticated()
      .pipe(
        filter((authenticated) => authenticated),
        take(1),
        switchMap(() => this.helpService.status()),
        takeUntil(this.destroy$),
      )
      .subscribe((status) => this.enabled.set(status.enabled));
  }

  protected togglePanel() {
    this.open.set(!this.open());

    if (!this.open()) {
      this.stopVoice();
    }
  }

  // Data answers can be dense (order lines, stock by warehouse); expanded, the
  // panel grows to a working size. The drag offset resets so the expanded panel
  // re-anchors bottom-right instead of inheriting a position chosen for the
  // small one.
  protected toggleExpanded() {
    this.expanded.set(!this.expanded());
    this.panelOffset.set({ x: 0, y: 0 });
  }

  // Push-to-talk: click to dictate, click again to stop; the recogniser also stops
  // itself on a silence pause. Errors (permission denied, no speech) just end
  // listening quietly, keeping whatever transcript arrived.
  protected toggleListening() {
    if (this.listening()) {
      this.recognition?.stop();
      return;
    }

    if (!HelpPanelComponent.speechRecognitionCtor) {
      return;
    }

    this.recognition = new HelpPanelComponent.speechRecognitionCtor();
    this.recognition.lang = this.locale;
    this.recognition.interimResults = true;

    this.dictationBase = this.question.trim() ? `${this.question.trim()} ` : '';

    // Recognition events arrive outside Angular's zone, so bindings would not update
    // without re-entering it.
    this.recognition.onresult = (event: any) => {
      let transcript = '';
      for (const result of event.results) {
        transcript += result[0].transcript;
      }
      this.zone.run(() => (this.question = this.dictationBase + transcript));
    };
    this.recognition.onerror = () => this.zone.run(() => this.listening.set(false));
    this.recognition.onend = () => this.zone.run(() => this.listening.set(false));

    this.listening.set(true);
    this.recognition.start();
  }

  protected toggleSpeakReplies() {
    this.speakReplies.set(!this.speakReplies());

    if (!this.speakReplies() && this.speechSupported) {
      window.speechSynthesis.cancel();
    }
  }

  protected clear() {
    this.messages = [];
    this.messageHtml = [];
    this.draft.set(undefined);
    this.purchaseDraft.set(undefined);
  }

  protected send() {
    const content = this.question.trim();
    if (!content || this.busy()) {
      return;
    }

    if (this.speechSupported) {
      window.speechSynthesis.cancel();
    }

    // Typing another message while a draft card is showing means the user is not
    // done ("sorry, one more item") — discard the card so the conversation returns
    // to the bottom of the chat. Stan re-proposes once they confirm it's complete.
    this.draft.set(undefined);
    this.purchaseDraft.set(undefined);

    this.question = '';
    this.addMessage({ role: HelpChatRoles.user, content, screen: this.currentScreen() });
    this.busy.set(true);

    this.helpService
      .chat({ messages: this.messages.slice(-HelpPanelComponent.historyLimit) })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.busy.set(false);
          this.addMessage({ role: HelpChatRoles.assistant, content: response.answer });
          this.warningPercent.set(response.warningPercent);
          if (response.draft) {
            this.draft.set(response.draft);
          }
          if (response.purchaseDraft) {
            this.purchaseDraft.set(response.purchaseDraft);
          }
        },
        error: () => {
          this.busy.set(false);
          this.addMessage({
            role: HelpChatRoles.assistant,
            content: $localize`Sorry, something went wrong. Please try again.`,
          });
        },
      });
  }

  protected discardDraft() {
    this.draft.set(undefined);
    this.purchaseDraft.set(undefined);
  }

  protected startPanelDrag(event: PointerEvent) {
    // Clicks on the header's own buttons (Clear, close) are not drags.
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }

    // Clamped so the header can never leave the viewport and strand the panel
    // (anchored bottom-right, so dragging left is the negative direction).
    this.beginDrag(event, this.panelOffset, (x, y) => ({
      x: Math.min(Math.max(x, -(window.innerWidth - 420)), 10),
      y: Math.min(Math.max(y, -Math.max(0, window.innerHeight - 600)), 20),
    }));
  }

  protected startLauncherDrag(event: PointerEvent) {
    this.launcherDragged = false;
    this.beginDrag(
      event,
      this.launcherOffset,
      (x, y) => ({
        x: Math.min(Math.max(x, -(window.innerWidth - 150)), 10),
        y: Math.min(Math.max(y, -(window.innerHeight - 90)), 10),
      }),
      () => (this.launcherDragged = true),
    );
  }

  // A drag that actually moved suppresses the click that follows it, so dragging
  // the button doesn't also open the panel.
  protected toggleFromLauncher() {
    if (this.launcherDragged) {
      this.launcherDragged = false;
      return;
    }

    this.togglePanel();
  }

  private beginDrag(
    event: PointerEvent,
    offset: WritableSignal<{ x: number; y: number }>,
    clamp: (x: number, y: number) => { x: number; y: number },
    onMoved?: () => void,
  ) {
    event.preventDefault();
    const start = { pointerX: event.clientX, pointerY: event.clientY, offsetX: offset().x, offsetY: offset().y };
    let passedThreshold = false;

    const move = (moveEvent: PointerEvent) => {
      const deltaX = moveEvent.clientX - start.pointerX;
      const deltaY = moveEvent.clientY - start.pointerY;

      // Movement below the threshold is a click, not a drag — don't jiggle.
      if (!passedThreshold) {
        if (
          Math.abs(deltaX) < HelpPanelComponent.dragThreshold &&
          Math.abs(deltaY) < HelpPanelComponent.dragThreshold
        ) {
          return;
        }
        passedThreshold = true;
        onMoved?.();
      }

      offset.set(clamp(start.offsetX + deltaX, start.offsetY + deltaY));
    };

    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };

    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }

  // Creation only ever happens here, on the user's explicit click — never from the
  // assistant itself. The order is created unconfirmed for review on its edit screen.
  protected createDraftOrder() {
    const draft = this.draft();
    if (!draft || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.helpService
      .createOrderDraft(draft)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.busy.set(false);
          this.draft.set(undefined);
          this.addMessage({
            role: HelpChatRoles.assistant,
            content: $localize`Order ${result.orderNo}:orderNo: created — opening it for you to review. It's unconfirmed until you click Confirm Order.`,
          });
          this.router.navigate(['/Sales/SalesOrder/Edit', result.orderId]);
        },
        error: () => {
          this.busy.set(false);
          this.addMessage({
            role: HelpChatRoles.assistant,
            content: $localize`Sorry, the order couldn't be created. Please check the draft and try again.`,
          });
        },
      });
  }

  // Same boundary as the sales draft: creation only ever happens on the user's
  // explicit click. The purchase order is created unconfirmed for review on its screen.
  protected createDraftPurchaseOrder() {
    const purchaseDraft = this.purchaseDraft();
    if (!purchaseDraft || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.helpService
      .createPurchaseOrderDraft(purchaseDraft)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.busy.set(false);
          this.purchaseDraft.set(undefined);
          this.addMessage({
            role: HelpChatRoles.assistant,
            content: $localize`Purchase order ${result.orderNo}:orderNo: created — opening it for you to review. It's unconfirmed until you confirm it on the purchase order screen.`,
          });
          this.router.navigate(['/Purchasing/PurchaseOrder/Edit', result.orderId]);
        },
        error: () => {
          this.busy.set(false);
          this.addMessage({
            role: HelpChatRoles.assistant,
            content: $localize`Sorry, the purchase order couldn't be created. Please check the draft and try again.`,
          });
        },
      });
  }

  // The route the user is on, with record ids (Guids/numbers) and the query string
  // stripped — e.g. "/Sales/SalesOrder/Edit/{guid}" becomes "Sales/SalesOrder/Edit".
  // Sent with each question so the assistant can tailor its answer to the screen.
  private currentScreen(): string {
    return this.router.url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment && !HelpPanelComponent.guidOrNumber.test(segment))
      .join('/');
  }

  private addMessage(message: HelpChatMessage) {
    this.messages.push(message);
    this.messageHtml.push(this.toHtml(message.content));

    if (message.role === HelpChatRoles.assistant) {
      this.speak(message.content);
    }

    // Scroll after the new message has rendered.
    setTimeout(() => {
      const list = this.messageList()?.nativeElement;
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    });
  }

  // The assistant answers in light markdown (bold labels, numbered steps, and — for
  // data answers — [text](/route) links to screens). Escape everything, then
  // re-introduce only bold, line breaks, and links whose route starts with a
  // whitelisted internal prefix — nothing else may pass through to [innerHTML].
  private toHtml(content: string): string {
    const escaped = content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // The route character class is deliberately narrow (path segments, Guids,
    // numbers): a URL carrying a query string, protocol or entity stays plain text.
    // The assistant sometimes writes "[text] (/route)" with a space or line break
    // before the bracket, so a little whitespace is tolerated there (Steve's first
    // live test, 2026-09-14 — every link rendered as raw text).
    const linked = escaped.replace(/\[([^\]\n]+)\]\s{0,2}\((\/[A-Za-z0-9/_-]+)\)/g, (match, text, route) =>
      HelpPanelComponent.linkPrefixes.some((prefix) => route.startsWith(prefix))
        ? `<a href="${route}">${text}</a>`
        : match,
    );
    return linked.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\r?\n/g, '<br />');
  }

  // Links in answers navigate in-app: one delegated handler on the message list,
  // because the anchors live in [innerHTML] where Angular bindings can't reach.
  protected onMessageClick(event: MouseEvent) {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor) {
      return;
    }

    // Every anchor in a message came through the whitelist in toHtml, but the
    // navigation re-checks it — the render-side rule and the click-side rule must
    // not be able to drift apart.
    event.preventDefault();
    const route = anchor.getAttribute('href');
    if (route && HelpPanelComponent.linkPrefixes.some((prefix) => route.startsWith(prefix))) {
      this.router.navigateByUrl(route);
    }
  }

  private speak(content: string) {
    if (!this.speakReplies() || !this.speechSupported) {
      return;
    }

    // One reply at a time - a new answer supersedes whatever is still being read.
    window.speechSynthesis.cancel();

    // Strip the markdown for the ear: bold markers go, a link reads as its text,
    // line breaks become pauses.
    const utterance = new SpeechSynthesisUtterance(
      content
        .replace(/\[([^\]\n]+)\]\s{0,2}\(\/[A-Za-z0-9/_-]+\)/g, '$1')
        .replace(/\*\*/g, '')
        .replace(/\r?\n+/g, '. '),
    );
    utterance.lang = this.locale;
    window.speechSynthesis.speak(utterance);
  }

  private stopVoice() {
    this.recognition?.abort();

    if (this.speechSupported) {
      window.speechSynthesis.cancel();
    }
  }

  override ngOnDestroy() {
    this.stopVoice();
    super.ngOnDestroy();
  }
}
